import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { FiSearch, FiCalendar, FiFilter, FiChevronDown, FiX, FiTrash2, FiEye, FiFileText, FiClock, FiCheck, FiXCircle } from 'react-icons/fi';

export default function MesDevis() {
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtres/tri
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // Suppression
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const realtimeRefreshTimerRef = useRef(null);

  const fetchDevis = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await api.getClientQuotes();
      setDevis(data.data || []);
    } catch (err) {
      setError(err.message || 'Erreur inconnue');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevis(false);
  }, [fetchDevis]);

  useEffect(() => {
    const relevantEvents = new Set([
      'price_proposed',
      'status_updated',
      'negotiation_accepted',
      'service_request_created',
      'new_service_request',
      'price_accepted',
      'notification',
    ]);
    const onRealtime = (evt) => {
      const eventName = evt?.detail?.event;
      const payload = evt?.detail?.payload;
      if (!relevantEvents.has(eventName)) return;
      if (
        eventName === 'notification' &&
        !['price_proposed', 'status_updated', 'negotiation_accepted', 'new_service_request', 'price_accepted'].includes(
          payload?.type
        )
      ) {
        return;
      }
      if (realtimeRefreshTimerRef.current) clearTimeout(realtimeRefreshTimerRef.current);
      realtimeRefreshTimerRef.current = setTimeout(() => {
        fetchDevis(true);
      }, 250);
    };
    window.addEventListener('liberty:realtime', onRealtime);
    return () => {
      window.removeEventListener('liberty:realtime', onRealtime);
      if (realtimeRefreshTimerRef.current) {
        clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }
    };
  }, [fetchDevis]);

  const skeletons = useMemo(() => Array.from({ length: 4 }).map((_, i) => (
    <div key={i} className="p-6 rounded-2xl border border-gray-700 bg-gray-800/50 animate-pulse">
      <div className="h-5 w-48 bg-gray-700 rounded" />
      <div className="mt-3 h-4 w-64 bg-gray-700 rounded" />
      <div className="mt-4 h-10 w-28 bg-gray-700 rounded-xl" />
    </div>
  )), []);

  const filtered = useMemo(() => {
    let items = [...devis];
    // Recherche simple sur départ/arrivée
    if (query.trim()) {
      const q = query.toLowerCase();
      items = items.filter(d =>
        (d.from_address || '').toLowerCase().includes(q) ||
        (d.to_address || '').toLowerCase().includes(q)
      );
    }
    // Filtre status
    if (statusFilter) {
      items = items.filter(d => (d.status || '').toLowerCase() === statusFilter.toLowerCase());
    }
    // Filtre dates
    if (dateStart) {
      items = items.filter(d => d.move_date && new Date(d.move_date) >= new Date(dateStart));
    }
    if (dateEnd) {
      const end = new Date(dateEnd);
      end.setHours(23, 59, 59, 999);
      items = items.filter(d => d.move_date && new Date(d.move_date) <= end);
    }
    // Tri
    items.sort((a, b) => {
      if (sortBy === 'date_asc') return new Date(a.move_date || 0) - new Date(b.move_date || 0);
      if (sortBy === 'status_asc') return String(a.status || '').localeCompare(String(b.status || ''));
      if (sortBy === 'status_desc') return String(b.status || '').localeCompare(String(a.status || ''));
      return new Date(b.move_date || 0) - new Date(a.move_date || 0); // date_desc (default)
    });
    return items;
  }, [devis, query, statusFilter, sortBy, dateStart, dateEnd]);

  const stats = useMemo(() => ({
    total: devis.length,
    pending: devis.filter(d => (d.status || '').toLowerCase() === 'pending').length,
    accepted: devis.filter(d => (d.status || '').toLowerCase() === 'accepted').length,
    rejected: devis.filter(d => (d.status || '').toLowerCase() === 'rejected').length,
  }), [devis]);

  const badge = (status) => {
    const s = (status || '').toLowerCase();
    const base = 'px-3 py-1 rounded-full text-xs font-medium';
    if (s === 'accepted') return `${base} bg-green-500/20 text-green-400 flex items-center gap-1`;
    if (s === 'rejected') return `${base} bg-red-500/20 text-red-400 flex items-center gap-1`;
    return `${base} bg-amber-500/20 text-amber-400 flex items-center gap-1`;
  };
  
  const getStatusIcon = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'accepted') return <FiCheck className="w-3 h-3" />;
    if (s === 'rejected') return <FiXCircle className="w-3 h-3" />;
    return <FiClock className="w-3 h-3" />;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  const remove = async (id) => {
    try {
      setDeleting(true);
      await api.deleteQuote(id);
      setDevis(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setError(e.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setConfirmId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-3xl p-8 border border-amber-500/20">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-yellow-500/5"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl lg:text-4xl font-bold text-white">Mes Devis</h1>
              <p className="text-amber-400 font-medium">Gérez et suivez tous vos devis en un seul endroit</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
              <div className="text-sm text-gray-400">Total</div>
              <div className="text-2xl font-bold text-white">{stats.total}</div>
            </div>
            <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
              <div className="text-sm text-gray-400">En attente</div>
              <div className="text-2xl font-bold text-amber-400">{stats.pending}</div>
            </div>
            <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
              <div className="text-sm text-gray-400">Acceptés</div>
              <div className="text-2xl font-bold text-green-400">{stats.accepted}</div>
            </div>
            <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
              <div className="text-sm text-gray-400">Refusés</div>
              <div className="text-2xl font-bold text-red-400">{stats.rejected}</div>
            </div>
          </div>
        </div>
      </div>

        {/* Filters */}
        <div className="bg-gray-800/50 p-5 rounded-2xl border border-gray-700 space-y-4">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-400 mb-1">Recherche</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <input 
                  value={query} 
                  onChange={e => setQuery(e.target.value)} 
                  placeholder="Départ ou arrivée" 
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex-1">
              <label className="block text-sm text-gray-400 mb-1">Statut</label>
              <div className="relative">
                <select 
                  value={statusFilter} 
                  onChange={e => setStatusFilter(e.target.value)} 
                  className="w-full pl-3 pr-10 py-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white appearance-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="accepted">Accepté</option>
                  <option value="rejected">Refusé</option>
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>
            
            <div className="flex-1">
              <label className="block text-sm text-gray-400 mb-1">Tri</label>
              <div className="relative">
                <select 
                  value={sortBy} 
                  onChange={e => setSortBy(e.target.value)} 
                  className="w-full pl-3 pr-10 py-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white appearance-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="date_desc">Date (récent)</option>
                  <option value="date_asc">Date (ancien)</option>
                  <option value="status_asc">Statut (A-Z)</option>
                  <option value="status_desc">Statut (Z-A)</option>
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-4 pt-2">
            <div className="flex-1">
              <label className="block text-sm text-gray-400 mb-1">Du</label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <input 
                  type="date" 
                  value={dateStart} 
                  onChange={e => setDateStart(e.target.value)} 
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex-1">
              <label className="block text-sm text-gray-400 mb-1">Au</label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <input 
                  type="date" 
                  value={dateEnd} 
                  onChange={e => setDateEnd(e.target.value)} 
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {(query || statusFilter || dateStart || dateEnd) && (
              <div className="flex items-end">
                <button 
                  onClick={() => {
                    setQuery('');
                    setStatusFilter('');
                    setDateStart('');
                    setDateEnd('');
                  }}
                  className="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl flex items-center gap-2 transition-colors"
                >
                  <FiX className="w-4 h-4" />
                  Réinitialiser
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quotes List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{skeletons}</div>
        ) : error ? (
          <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-6 py-4 rounded-2xl text-center">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <FiFileText className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Aucun devis trouvé</h3>
            <p className="text-gray-400 max-w-md mx-auto">
              {query || statusFilter || dateStart || dateEnd 
                ? "Aucun devis ne correspond à vos critères de recherche."
                : "Vous n'avez pas encore de devis. Créez votre premier devis pour commencer."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((d) => (
              <div key={d.id} className="bg-gray-800/50 border border-gray-700 rounded-2xl overflow-hidden hover:border-amber-500/30 transition-colors">
                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FiFileText className="w-6 h-6 text-amber-500" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg font-semibold text-white truncate">
                            {d.from_address || 'Non spécifié'} 
                            <span className="mx-2 text-amber-400">→</span> 
                            {d.to_address || 'Non spécifié'}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-gray-400">
                              {new Date(d.move_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                            <span className="text-gray-600">•</span>
                            <span className="text-sm text-gray-400">
                              {d.volume_m3 ? `${d.volume_m3} m³` : 'Volume non spécifié'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="hidden md:block">
                        <span className={badge(d.status)}>
                          {getStatusIcon(d.status)}
                          {d.status === 'accepted' ? 'Accepté' : d.status === 'rejected' ? 'Refusé' : 'En attente'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Link 
                          to={`/client/quotes/${d.id}`}
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-xl flex items-center gap-2 transition-colors"
                        >
                          <FiEye className="w-4 h-4" />
                          <span className="hidden sm:inline">Voir</span>
                        </Link>
                        
                        <button 
                          onClick={() => setConfirmId(d.id)}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
                          aria-label="Supprimer"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      {confirmId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Confirmer la suppression</h2>
              <button 
                onClick={() => setConfirmId(null)}
                className="text-gray-400 hover:text-white"
                aria-label="Fermer"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-gray-300 mb-6">
              Voulez-vous vraiment supprimer ce devis ? Cette action est irréversible.
            </p>
            
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setConfirmId(null)}
                className="px-5 py-2.5 rounded-xl border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Annuler
              </button>
              <button 
                onClick={() => remove(confirmId)} 
                disabled={deleting}
                className="px-5 py-2.5 rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Suppression...
                  </>
                ) : (
                  <React.Fragment>
                    <FiTrash2 className="w-4 h-4" />
                    Supprimer
                  </React.Fragment>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}